import { LRUCache } from '@portal/common/utility/cache/lru-cache';

type CachedRequestOptions = {
    skipCache?: boolean;
};

const createCachedRequest = <TParams, TResponse>(
    request: (params: TParams) => Promise<TResponse>,
    maxCacheSize: number
): ((params: TParams, options?: CachedRequestOptions) => Promise<TResponse>) => {
    const responseCache = new LRUCache<string, TResponse>(maxCacheSize);
    const pendingRequests = new Map<string, Promise<TResponse>>();

    return (params: TParams, { skipCache = false }: CachedRequestOptions = {}) => {
        if (skipCache) {
            return request(params);
        }

        const cacheKey = JSON.stringify(params);

        if (responseCache.has(cacheKey)) {
            return Promise.resolve(responseCache.get(cacheKey));
        }

        if (pendingRequests.has(cacheKey)) {
            return pendingRequests.get(cacheKey);
        }

        const pendingRequest = request(params)
            .then((response) => {
                responseCache.set(cacheKey, response);
                pendingRequests.delete(cacheKey);
                return response;
            })
            .catch((error) => {
                pendingRequests.delete(cacheKey);
                throw error;
            });

        pendingRequests.set(cacheKey, pendingRequest);
        return pendingRequest;
    };
};

export default createCachedRequest;
